import {createFeature, createReducer, on} from '@ngrx/store';
import {PortfolioSimulatorActions} from './portfolio-simulator.actions';
import {SimulatorInputsState} from '../models/simulator-inputs';

const initialState: SimulatorInputsState = {
  version: '',
  provider: 'pmf',
  priceDataset: '',
  weatherYear: 'all',
  assetsByVersion: {},
  assetsLoading: false,
  offtakesByVersion: {},
  offtakesLoading: false,
  versions: [],
  versionsLoading: false,
  versionsLoaded: false,
  priceDatasets: [],
  priceDatasetsLoading: false,
  priceDatasetsLoaded: false,
  simulationResults: [],
  simulationResultsLoading: false,
  simulationResultsLoaded: false,
};

export const portfolioSimulatorFeature = createFeature({
  name: 'portfolioSimulator',
  reducer: createReducer(
    initialState,
    on(PortfolioSimulatorActions.init, (state) => ({
      ...state,
      assetsLoading: !(state.version in state.assetsByVersion),
      offtakesLoading: !(state.version in state.offtakesByVersion),
      versionsLoading: !state.versionsLoaded,
      priceDatasetsLoading: !state.priceDatasetsLoaded,
    })),
    on(PortfolioSimulatorActions.updateInputs, (state, action) => ({
      ...state,
      ...(action.version !== undefined && {version: action.version}),
      ...(action.provider !== undefined && {provider: action.provider}),
      ...(action.priceDataset !== undefined && {
        priceDataset: action.priceDataset,
      }),
      ...(action.weatherYear !== undefined && {
        weatherYear: action.weatherYear,
      }),
      // Version change: invalidate assets/offtakes cache + price datasets cross-filter
      ...(action.version !== undefined &&
        action.version !== state.version && {
          assetsLoading: !(action.version in state.assetsByVersion),
          offtakesLoading: !(action.version in state.offtakesByVersion),
          priceDatasetsLoading: true,
          priceDatasetsLoaded: false,
        }),
      // Price dataset change: invalidate versions cross-filter
      ...(action.priceDataset !== undefined &&
        action.priceDataset !== state.priceDataset && {
          versionsLoading: true,
          versionsLoaded: false,
        }),
    })),
    on(PortfolioSimulatorActions.loadAssetsSuccess, (state, {assets, version}) => ({
      ...state,
      assetsByVersion: {...state.assetsByVersion, [version]: assets},
      assetsLoading: false,
    })),
    on(PortfolioSimulatorActions.loadAssetsFailure, (state) => ({
      ...state,
      assetsLoading: false,
    })),
    on(
      PortfolioSimulatorActions.loadOfftakesSuccess,
      (state, {offtakes, version}) => ({
        ...state,
        offtakesByVersion: {...state.offtakesByVersion, [version]: offtakes},
        offtakesLoading: false,
      }),
    ),
    on(PortfolioSimulatorActions.loadOfftakesFailure, (state) => ({
      ...state,
      offtakesLoading: false,
    })),
    on(PortfolioSimulatorActions.loadVersionsSuccess, (state, {versions}) => ({
      ...state,
      versions,
      versionsLoading: false,
      versionsLoaded: true,
    })),
    on(PortfolioSimulatorActions.loadVersionsFailure, (state) => ({
      ...state,
      versionsLoading: false,
      // versionsLoaded intentionally stays false: the next updateInputs will retry the fetch automatically
    })),
    on(PortfolioSimulatorActions.loadPriceDatasetsSuccess, (state, {priceDatasets}) => ({
      ...state,
      priceDatasets,
      priceDatasetsLoading: false,
      priceDatasetsLoaded: true,
    })),
    on(PortfolioSimulatorActions.loadPriceDatasetsFailure, (state) => ({
      ...state,
      priceDatasetsLoading: false,
      // priceDatasetsLoaded intentionally stays false: the next updateInputs will retry the fetch automatically
    })),
    on(PortfolioSimulatorActions.displayResults, (state) => ({
      ...state,
      simulationResultsLoading: true,
      simulationResultsLoaded: false,
    })),
    on(
      PortfolioSimulatorActions.loadSimulationResultsSuccess,
      (state, {results}) => ({
        ...state,
        simulationResults: results,
        simulationResultsLoading: false,
        simulationResultsLoaded: true,
      }),
    ),
    on(PortfolioSimulatorActions.loadSimulationResultsFailure, (state) => ({
      ...state,
      simulationResultsLoading: false,
      // simulationResultsLoaded intentionally stays false: no data was loaded
    })),
  ),
});

export const {
  selectVersion,
  selectProvider,
  selectPriceDataset,
  selectWeatherYear,
  selectAssetsByVersion,
  selectAssetsLoading,
  selectOfftakesByVersion,
  selectOfftakesLoading,
  selectVersions,
  selectVersionsLoading,
  selectVersionsLoaded,
  selectPriceDatasets,
  selectPriceDatasetsLoading,
  selectPriceDatasetsLoaded,
  selectSimulationResults,
  selectSimulationResultsLoading,
  selectSimulationResultsLoaded,
} = portfolioSimulatorFeature;
